import React from 'react';
import Form, { FieldSet } from '../../components/Form';
import ModeToggle from '../../components/ModeToggle';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import type { BillingSummaryStatus } from '../../graphql/generated/graphql';
import { PlanInterval, PlanTier } from '../../graphql/generated/graphql';
import * as styles from './PlanChooser.module.scss';
import PlanRow from './PlanRow';

const BILLING_PRICING_FRAGMENT = gql(`
  fragment PlanChooser_billingPricing on BillingPricing {
    free {
      ...PlanRow_billingTier
    }
    startup {
      ...PlanRow_billingTier
    }
    business {
      ...PlanRow_billingTier
    }
  }
`);

const BILLING_COUPON_FRAGMENT = gql(`
  fragment PlanChooser_billingCoupon on BillingCoupon {
    ...PlanRow_billingCoupon
  }
`);

interface PlanChooserProps {
  billingCoupon?: DocumentType<typeof BILLING_COUPON_FRAGMENT> | null;
  status: BillingSummaryStatus;
  selectedInterval: PlanInterval;
  selectedTier: PlanTier;
  onChange: (tier: PlanTier, interval: PlanInterval) => void;
  billingPricing: DocumentType<typeof BILLING_PRICING_FRAGMENT>;
}

const PlanChooser = ({
  billingCoupon,
  status,
  selectedInterval,
  selectedTier,
  onChange,
  billingPricing: { free, startup, business },
}: PlanChooserProps) => {
  const isAnnual = selectedInterval === PlanInterval.Yearly;
  const commonProps = {
    billingCoupon,
    interval: selectedInterval,
  };

  return (
    <Form initialValues={{}}>
      <FieldSet>
        <div className={styles.header}>
          <legend className={styles.legend}>Choose a plan</legend>
          <ModeToggle
            left="Monthly"
            right="Annual"
            leftActive={!isAnnual}
            onToggle={() =>
              onChange(
                selectedTier,
                isAnnual ? PlanInterval.Monthly : PlanInterval.Yearly,
              )
            }
          />
        </div>
        <div className={styles.rows}>
          <PlanRow
            {...commonProps}
            planTier={PlanTier.Free}
            billingTier={free}
            selected={selectedTier === PlanTier.Free}
            status={status}
            userLimit={10}
            tooltip={`Only 10 seats allowed,\nguests included. Only public\ntopics are supported.`}
            onSelect={() => onChange(PlanTier.Free, selectedInterval)}
          />
          <PlanRow
            {...commonProps}
            planTier={PlanTier.Startup}
            billingTier={startup}
            selected={selectedTier === PlanTier.Startup}
            tooltip={`Unlimited posts, topics,\nand templates. Support via\nemail and in-app messaging.`}
            onSelect={() => onChange(PlanTier.Startup, selectedInterval)}
          />
          <PlanRow
            {...commonProps}
            planTier={PlanTier.Business}
            billingTier={business}
            selected={selectedTier === PlanTier.Business}
            tooltip={`Priority support and\npremium integrations like\nOkta, Office, and Zendesk`}
            onSelect={() => onChange(PlanTier.Business, selectedInterval)}
            recommended
          />
          <PlanRow
            {...commonProps}
            planTier={PlanTier.Enterprise}
            tooltip={`Access to all features\nand unlimited analytics,\nhistory, and integrations`}
            minimumUserLimit={100}
          />
        </div>
      </FieldSet>
    </Form>
  );
};

export default PlanChooser;
